import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, ShieldCheck, Clock3, RefreshCw } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const CRIT = {
  alta: { cls: "border-rose-300 bg-rose-50 text-rose-800", txt: "Coincidencia crítica" },
  media: { cls: "border-amber-300 bg-amber-50 text-amber-800", txt: "Coincidencia parcial" },
  ninguna: { cls: "border-emerald-300 bg-emerald-50 text-emerald-800", txt: "Sin coincidencias" },
};

export function AcreedorScreeningDialog({ acreedorId, open, onOpenChange, onRecotejado }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recotejando, setRecotejando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !acreedorId) return;
    setLoading(true);
    setError("");
    api.get(`/finanzas/acreedores/${acreedorId}/screening`)
      .then((r) => setData(r.data))
      .catch((e) => setError(apiError(e)))
      .finally(() => setLoading(false));
  }, [open, acreedorId]);

  const recotejar = async () => {
    setRecotejando(true);
    try {
      const r = await api.post(`/finanzas/acreedores/${acreedorId}/recotejar`);
      setData(r.data);
      toast.success("Acreedor recotejado contra las listas vigentes.");
      onRecotejado?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRecotejando(false);
    }
  };

  const crit = CRIT[data?.criticidad] || CRIT.ninguna;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="acreedor-screening-dialog">
        <DialogHeader>
          <DialogTitle>Cotejo de listas — acreedor de fondeo</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="py-10 text-center text-slate-400" data-testid="acreedor-screening-loading">
            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
          </div>
        )}

        {!loading && error && (
          <p className="py-6 text-sm text-rose-700" data-testid="acreedor-screening-error">{error}</p>
        )}

        {!loading && !error && data && (
          <div className="space-y-4">
            <div>
              <p className="font-heading text-lg font-semibold text-slate-900"
                 data-testid="acreedor-screening-nombre">{data.nombre}</p>
              <p className="font-mono text-xs text-slate-500" data-testid="acreedor-screening-rfc">
                {data.rfc || "Sin RFC capturado"}
              </p>
            </div>

            <div className={`flex items-start gap-3 rounded-lg border p-3 ${crit.cls}`}
                 data-testid="acreedor-screening-veredicto">
              {data.criticidad === "ninguna"
                ? <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
                : <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" />}
              <div className="text-sm">
                <p className="font-semibold">{crit.txt}</p>
                <p className="mt-0.5 text-xs">
                  {data.criticidad === "ninguna"
                    ? "La contraparte se cruzó contra las listas configuradas y no se encontró coincidencia."
                    : "El registro NO se bloqueó. Esta contraparte ENTREGA recursos a la entidad: revisa la coincidencia en Listas y sanciones PLD y confírmala o descártala antes de disponer del fondeo."}
                </p>
                {data.lpb && (
                  <p className="mt-1 text-xs font-semibold" data-testid="acreedor-screening-lpb">
                    Coincidencia en lista de sanción (LPB / OFAC / ONU / SAT 69-B).
                  </p>
                )}
                {data.pep && (
                  <p className="mt-1 text-xs font-semibold" data-testid="acreedor-screening-pep">
                    Coincidencia en catálogo PEP.
                  </p>
                )}
              </div>
            </div>

            {data.screening_pendiente && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                   data-testid="acreedor-screening-pendiente">
                <Clock3 className="mt-0.5 h-5 w-5 shrink-0" />
                <div>
                  <p className="font-semibold">Cotejo externo PENDIENTE</p>
                  <p className="mt-0.5 text-xs">
                    La contraparte se registró, pero la consulta al proveedor no pudo completarse
                    {data.screening_pendiente_motivo ? ` (${data.screening_pendiente_motivo})` : ""}.
                    El cotejo local sí se ejecutó.
                  </p>
                </div>
              </div>
            )}

            {data.recotejo_pendiente && (
              <div className="flex items-start justify-between gap-3 rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900"
                   data-testid="acreedor-recotejo-pendiente">
                <div>
                  <p className="font-semibold">Hay listas más nuevas que este cotejo</p>
                  <p className="mt-0.5 text-xs">
                    {data.listas_nuevas?.map((l) => l.etiqueta).join(" · ")}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={recotejar} disabled={recotejando}
                        data-testid="acreedor-recotejar-btn">
                  {recotejando
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <RefreshCw className="mr-2 h-4 w-4" />}
                  Recotejar
                </Button>
              </div>
            )}

            {data.coincidencias?.length > 0 && (
              <div className="rounded-lg border border-slate-200" data-testid="acreedor-screening-coincidencias">
                <div className="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Coincidencias encontradas ({data.total_coincidencias})
                </div>
                <ul className="divide-y divide-slate-100">
                  {data.coincidencias.map((c) => (
                    <li key={c.match_id} className="px-3 py-2 text-sm"
                        data-testid={`acreedor-coincidencia-${c.match_id?.slice(0, 6)}`}>
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-medium text-slate-900">{c.lista_label || c.lista}</span>
                        <Badge variant="outline" className={c.critica ? "border-rose-300 text-rose-700" : ""}>
                          score {c.score}
                        </Badge>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-600">
                        Registro: {c.registro} · {c.tipo} · {c.fuente}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-xs text-slate-500" data-testid="acreedor-screening-sello">
              <p>
                Corrida {data.run?.folio || "—"} · {(data.run?.fecha || "").slice(0, 19).replace("T", " ")}
                {data.run?.origen ? ` · ${data.run.origen}` : ""}
              </p>
              {data.run?.listas_cruzadas?.length > 0 && (
                <p className="mt-1">Cruzado contra: {data.run.listas_cruzadas.join(" · ")}</p>
              )}
              <p className="mt-1">
                Vista acotada por segregación de funciones: monto, tasa, plazo y datos contables del
                préstamo no se exponen en esta ruta.
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
